import React, { useState } from 'react';
import { UserCogIcon } from 'lucide-react';
import { EmployeeSetting } from '../../types/commission';
import { Card } from '../sf/Card';
import { ExampleDataBadge } from '../sf/ExampleDataBadge';
import { Td, Th } from '../sf/DataTable';
import { NewBadge } from '../sf/NewBadge';
import { Switch } from '../sf/Switch';
interface EmployeeRulesCardProps {
  settings: EmployeeSetting[];
  editable: boolean;
  onToggleBonus: (setting: EmployeeSetting, bonusOn: boolean) => void;
}
function BpsCell({
  value
}: {
  value?: number;
}) {
  if (value === undefined) return <span className="text-sf-weak" data-id="element-83" data-mp="3ptfgeomxsm1s">—</span>;
  return <span className="flex flex-wrap items-center gap-1.5 whitespace-nowrap" data-id="element-84" data-mp="3psw0fdguk7qe">
      <span className="font-semibold tabular-nums" data-id="element-85" data-mp="3lsvlzstqzwjc">{value}</span>
      <span className="text-xs text-sf-weak" data-id="element-86" data-mp="1vfeic5z7o32r">read-only</span>
      <ExampleDataBadge data-id="element-87" data-mp="1vfod06v7ayes" />
    </span>;
}
export function EmployeeRulesCard({
  settings,
  editable,
  onToggleBonus
}: EmployeeRulesCardProps) {
  const [rows, setRows] = useState(settings);
  function handleToggle(setting: EmployeeSetting, bonusOn: boolean) {
    setRows(prev => prev.map(row => row.id === setting.id ? {
      ...row,
      bonusOn
    } : row));
    onToggleBonus(setting, bonusOn);
  }
  return <Card title="Employee rules" icon={UserCogIcon} iconBg="#5A1BA9" meta="Employee-record BPS by fee type when Custom Commission is on (BPS_Base__c custom path): LO row → Custom Base BPS · Team Lead row → Team Lead BPS · Director row → Director BPS. TL / Director rows carry no bonus BPS." bodyClassName="" actions={<ExampleDataBadge data-id="element-89" data-mp="17qictf6bvej4" />} data-id="element-88" data-mp="oz7o94o2rdue">
      <div className="overflow-x-auto" data-id="element-90" data-mp="17rjsjwyagp76">
        <table className="w-full border-collapse" data-id="element-91" data-mp="1cq3dpv525wyh">
          <thead data-id="element-92" data-mp="1cosem8mk5ddp">
            <tr data-id="element-93" data-mp="1cpbt1fd01jmz">
              <Th data-id="element-94" data-mp="1cnqz9ryie4q6">Employee</Th>
              <Th data-id="element-95" data-mp="39iwy8o82z14a">Branch</Th>
              <Th data-id="element-96" data-mp="39joorkmtm8np">Base Source</Th>
              <Th data-id="element-97" data-mp="39kq2jwv81994">Custom Rule</Th>
              <Th data-id="element-98" data-mp="3egygj6xomwep">Custom Base BPS</Th>
              <Th data-id="element-99" data-mp="3ehzyzwh12l6g">Team Lead BPS</Th>
              <Th data-id="element-100" data-mp="36gsfgher2yyf">Director BPS</Th>
              <Th data-id="element-101" data-mp="36htpqjnxzhf3">
                <span className="inline-flex items-center gap-1.5" data-id="element-102" data-mp="36haipxztydfu">
                  Bonus switch <NewBadge label="NEW" data-id="element-103" data-mp="32g8fz76pfj94" />
                </span>
              </Th>
            </tr>
          </thead>
          <tbody data-id="element-104" data-mp="3ggj9khzefbxz">
            {rows.map(row => <tr key={row.id} data-id="element-105" data-mp="3bg4xmouofhuo">
                <Td className="whitespace-nowrap font-semibold" data-id="element-106" data-mp="3betzp5nwx4bh">{row.name}</Td>
                <Td className="text-sf-weak" data-id="element-107" data-mp="3pf4qy9t4wkx6">{row.branch}</Td>
                <Td className="text-sf-weak" data-id="element-108" data-mp="3pda0yh30a2m8">{row.baseSource}</Td>
                <Td data-id="element-109" data-mp="3ld9kys09djd2">
                  {row.customRule ? <>
                      <span className="block" data-id="element-110" data-mp="2z0fogudhapo">{row.customRule}</span>
                      <span className="font-mono text-xs text-sf-weak" data-id="element-111" data-mp="3u6mriwr8wjhm">{row.customRuleKey}</span>
                    </> : <span className="text-sf-weak" data-id="element-112" data-mp="cyrakjmuhrqa">—</span>}
                </Td>
                <Td data-id="element-113" data-mp="80pso215nyge">
                  <BpsCell value={row.customBaseBps} data-id="element-114" data-mp="7x1yr1rsta15" />
                </Td>
                <Td data-id="element-115" data-mp="rm9fnj4bkod8">
                  <BpsCell value={row.roleBps?.field === 'Team Lead BPS' ? row.roleBps.value : undefined} data-id="element-116" data-mp="riu0yhek5hcc" />
                </Td>
                <Td data-id="element-117" data-mp="hn0sb93ym7zu">
                  <BpsCell value={row.roleBps?.field === 'Director BPS' ? row.roleBps.value : undefined} data-id="element-118" data-mp="hnjxrqcfb3wz" />
                </Td>
                <Td data-id="element-119" data-mp="mkadgckbfxhv">
                  {row.roleBps?.field === 'Director BPS' ? <span className="text-xs text-sf-weak" data-id="element-120" data-mp="7vwb02qtab1u">—</span> : <Switch checked={row.bonusOn} label={`Monthly Bonus for ${row.name}`} disabled={!editable} onChange={next => handleToggle(row, next)} data-id="element-121" data-mp="copiuzhktzi5" />}
                </Td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </Card>;
}